import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RequirementDismissalReason } from '@scoutaround/script-breakdown-shared';

import { api, ApiError, type ReviewRequirement } from '../../lib/api.js';
import { RequirementTag, SourcingTag } from '../../components/StatusTag.js';
import { Callout } from '../../components/Callout.js';
import { ConversationEntry } from '../../components/conversations/ConversationEntry.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Disclosure } from '../../components/ui/Disclosure.js';
import { SegmentedFilter } from '../../components/ui/SegmentedFilter.js';
import { SearchField } from '../../components/ui/SearchField.js';

type Filter = 'ALL' | 'NEEDS_REVIEW' | 'UNREVIEWED' | 'APPROVED';

/**
 * The closed reason set, rendered as READABLE ENGLISH.
 *
 * Never a percentage. "NEEDS REVIEW — the screenplay does not establish whether
 * the interior and exterior are the same place" tells a producer what to look at;
 * "63%" tells them nothing they can act on and invites false precision about a
 * judgement the model cannot actually quantify.
 */
export const CONFIDENCE_REASON: Record<string, string> = {
  MULTIPLE_SCRIPT_NAMES: 'The screenplay calls this location by more than one name.',
  INT_EXT_UNCLEAR:
    'The screenplay does not establish whether the interior and exterior must be the same practical location.',
  SAME_LOCATION_UNCLEAR: 'It is unclear whether these scenes share one location.',
  CATEGORY_AMBIGUOUS: 'The kind of place is ambiguous from the text.',
  INSUFFICIENT_DESCRIPTION: 'The screenplay describes this location too thinly to classify it.',
  CONFLICTING_SCENES: 'Scenes describe this location in ways that conflict.',
  MANUAL_REQUIREMENT: 'Added by a person, not extracted from the screenplay.',
  OTHER: 'Flagged for review.',
};

/**
 * Why a location is not needed (BI gap 25), in the order a producer reaches for
 * them. "Not in the script" is the reading's false positive; "Not needed" is a
 * production choice about a place that IS there — the two must not share a count.
 */
const DISMISSAL_REASONS: readonly (readonly [RequirementDismissalReason, string])[] = [
  ['NOT_IN_SCRIPT', 'Not in the script'],
  ['DUPLICATE', 'Duplicate'],
  ['WRONG_TYPE', 'Wrong kind of place'],
  ['NOT_NEEDED', 'Not needed'],
  ['OTHER', 'Other'],
];

const CONSTRAINT_HEADING: Record<string, string> = {
  MUST_HAVE: 'Must have',
  NICE_TO_HAVE: 'Nice to have',
  EXCLUDE: 'Exclude',
  INFORMATIONAL: 'Noted',
};

export function ScriptReview({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('ALL');
  const [search, setSearch] = useState('');
  const [conflict, setConflict] = useState<string | null>(null);
  /** The requirement whose "why" is open — one at a time. */
  const [dismissing, setDismissing] = useState<string | null>(null);

  /**
   * FOCUS FOLLOWS THE PICKER. Opening it swaps the "We don't need this" button for the
   * reason buttons, so the focused element disappears and a keyboard or screen-reader user
   * is dropped to the top of the page. Focus goes to the first reason on open, and back to
   * the button that opened it on close (it is gone once the dismissal lands, which is fine:
   * the row then says it is dismissed).
   */
  const firstReasonRef = useRef<HTMLButtonElement | null>(null);
  const dismissButtons = useRef(new Map<string, HTMLButtonElement>());
  const lastDismissing = useRef<string | null>(null);

  useEffect(() => {
    if (dismissing !== null) {
      firstReasonRef.current?.focus();
    } else if (lastDismissing.current !== null) {
      dismissButtons.current.get(lastDismissing.current)?.focus();
    }
    lastDismissing.current = dismissing;
  }, [dismissing]);

  const requirements = useQuery({
    queryKey: ['requirements', projectId],
    queryFn: () => api.listRequirements(projectId),
  });

  const progress = useQuery({
    queryKey: ['review-progress', projectId],
    queryFn: () => api.reviewProgress(projectId),
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['matchable', projectId] }),
    ]);
  };

  /**
   * Every mutation carries the version the producer was LOOKING AT. Two people
   * in Script Review at once is the normal case, and a lost update silently
   * discards a colleague's decision — so a stale write is refused and the screen
   * says so rather than pretending it worked.
   */
  const onConflict = (error: unknown) => {
    if (error instanceof ApiError && error.status === 409) {
      setConflict('Someone else changed this requirement while you were reading it. Reloaded.');
      void refresh();
      return true;
    }
    return false;
  };

  const approve = useMutation({
    mutationFn: (r: ReviewRequirement) => api.approveRequirement(projectId, r.id, r.rowVersion),
    onSuccess: refresh,
    onError: onConflict,
  });

  const dismiss = useMutation({
    mutationFn: ({
      r,
      reasonCode,
    }: {
      r: ReviewRequirement;
      reasonCode: RequirementDismissalReason | null;
    }) =>
      api.dismissRequirement(
        projectId,
        r.id,
        r.rowVersion,
        reasonCode === null ? {} : { reasonCode },
      ),
    onSuccess: async () => {
      setDismissing(null);
      await refresh();
    },
    onError: (error: unknown) => {
      setDismissing(null);
      onConflict(error);
    },
  });

  const sourcing = useMutation({
    mutationFn: ({ r, to }: { r: ReviewRequirement; to: 'ACTIVE' | 'PAUSED' | 'INACTIVE' }) =>
      api.setSourcing(projectId, r.id, r.rowVersion, to),
    onSuccess: refresh,
    onError: onConflict,
  });

  const matches = (r: ReviewRequirement, f: Filter): boolean => {
    if (f === 'APPROVED') return r.status === 'APPROVED';
    if (f === 'UNREVIEWED') return r.status === 'PROPOSED' || r.status === 'DRAFT';
    if (f === 'NEEDS_REVIEW') return r.confidenceBand !== 'HIGH' || r.needsRevisionReview;
    return true;
  };

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (requirements.data ?? []).filter((r) => q === '' || r.title.toLowerCase().includes(q));
  }, [requirements.data, search]);

  const visible = searched.filter((r) => matches(r, filter));
  /** Each filter's count over what the search leaves — known only once the rows are in. */
  const countOf = (f: Filter): number | undefined =>
    requirements.data === undefined ? undefined : searched.filter((r) => matches(r, f)).length;

  const unresolved = (requirements.data ?? []).filter(
    (r) => r.status === 'PROPOSED' || r.status === 'DRAFT',
  );

  const p = progress.data;

  return (
    <div data-testid="script-review">
      {/* Progress header — the design's "14 requirements · 8 approved · 3 need
          review" line, so a producer always knows how far through they are. */}
      <PageHeader
        title="Script review"
        lede={
          p ? (
            <span data-testid="review-header">
              {p.total} location{p.total === 1 ? '' : 's'} asked for · {p.approved} confirmed ·{' '}
              {p.proposed} awaiting your decision · {p.dismissed} not needed
            </span>
          ) : undefined
        }
        status={
          unresolved.length > 0 ? (
            <span className="chip" data-testid="unresolved-count">
              {unresolved.length} unresolved
            </span>
          ) : undefined
        }
      />

      {/*
        THE TWO AXES, SAID IN WORDS (ADR 0004) — folded, because a producer
        needs them once and reads past them every time after.

        "Is this a real location we need?" and "are we looking for it right
        now?" are SEPARATE questions, and answering the first does not start
        spending on the second.
      */}
      <div style={{ marginTop: 8 }}>
        <Disclosure summary="Two separate decisions on every line — how this list works" testId="review-explainer">
          <p className="mm" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.7 }}>
            Each line below is a location the screenplay asks for. There are{' '}
            <strong>two separate decisions</strong>, and one does not imply the other.
          </p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
            <li>
              <strong>Is it real?</strong> <em>We need this location</em> accepts it — the production
              does need this location. <em>We don&apos;t need this</em> rejects it, for the ones
              extraction got wrong (a set, a vehicle interior, a place that is really the same as
              another). A rejection is remembered: a later draft cannot bring it back under a
              reworded name.
            </li>
            <li>
              <strong>Are we looking yet?</strong> <em>Start searching</em> begins the search —
              matching runs against the catalogue and candidates appear under Locations.{' '}
              <em>Pause searching</em> stops it, keeping the requirement. Only a confirmed requirement
              can be searched for, because searching for an interpretation nobody has checked spends
              money on a guess.
            </li>
          </ul>
        </Disclosure>
      </div>

      {p?.reviewComplete ? (
        <div style={{ marginTop: 14 }}>
          <Callout tone="ok" title="Master location list — review complete" testId="review-complete">
            Every proposal has been resolved. That is a statement about the review; it does not mean
            every requirement is being sourced.
          </Callout>
        </div>
      ) : null}

      {conflict ? (
        <div style={{ marginTop: 14 }}>
          <Callout tone="warn" title="Reloaded" testId="conflict-notice">
            {conflict}
          </Callout>
        </div>
      ) : null}

      {/* Filters and search */}
      <div className="ui-toolbar" style={{ marginTop: 14 }}>
        <SegmentedFilter<Filter>
          label="Show requirements"
          testIdPrefix="filter"
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'ALL', label: 'All', count: countOf('ALL') },
            { id: 'NEEDS_REVIEW', label: 'Needs a closer look', count: countOf('NEEDS_REVIEW') },
            {
              id: 'UNREVIEWED',
              label: 'Awaiting your decision',
              count: countOf('UNREVIEWED'),
              tone: (countOf('UNREVIEWED') ?? 0) > 0 ? 'attention' : undefined,
            },
            { id: 'APPROVED', label: 'Confirmed', count: countOf('APPROVED') },
          ]}
        />
        <span className="ui-toolbar-end" style={{ minWidth: 260 }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search requirements"
            label="Search requirements"
            testId="review-search"
          />
        </span>
      </div>

      {requirements.isPending ? <p className="mm" style={{ marginTop: 18 }}>Loading…</p> : null}

      {visible.length === 0 && !requirements.isPending ? (
        <p className="mm" style={{ fontSize: 13, marginTop: 20 }} data-testid="review-empty">
          Nothing matches this filter.
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: 12, marginTop: 18 }}>
        {visible.map((r) => (
          <article
            key={r.id}
            className="card1"
            style={{ padding: '18px 20px' }}
            data-testid={`review-card-${r.id}`}
          >
            <header style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <h3 className="dh" style={{ fontSize: 20 }}>
                {r.title}
              </h3>
              <RequirementTag status={r.status} />
              <SourcingTag status={r.sourcingStatus} />
              <ConversationEntry projectId={projectId} anchorType="REQUIREMENT" anchorId={r.id} label={r.title} />
              {r.origin === 'MANUAL' ? (
                // Labelled because regeneration can never remove it, and a
                // producer should know which rows are theirs.
                <span className="chip" data-testid={`human-added-${r.id}`}>
                  human added
                </span>
              ) : null}
              {/*
                "Scenes 4" WAS READ AS "four scenes". It never meant that — it
                is scene NUMBER 4 — and the plural label made a list of scene
                numbers indistinguishable from a count of them. Most
                requirements here link exactly one scene, so the misreading was
                the common case, not the edge.

                Singular when there is one, and the numbers are always spelled
                out: "which scenes need this location" is the question a
                producer is actually asking, and a count cannot answer it.
              */}
              <span
                className="mm"
                style={{ fontSize: 11.5, marginLeft: 'auto' }}
                data-testid={`req-scenes-${r.id}`}
              >
                {r.scenes.length === 0
                  ? 'Not linked to a scene'
                  : `${r.scenes.length === 1 ? 'Scene' : 'Scenes'} ${r.scenes
                      .map((s) => s.sceneNumber ?? String(s.ordinal ?? ''))
                      .join(', ')}`}
              </span>
            </header>

            {/* A BAND AND A REASON, never a number.
                Once the requirement is approved this becomes HISTORY rather than
                a live flag: a human edit never changes the AI's band (lifecycle
                status and confidence are separate axes), so the same text left
                in warning colours would read as an unresolved complaint about a
                decision the producer has already made. */}
            {r.confidenceBand !== 'HIGH' ? (
              r.status === 'APPROVED' ? (
                <p
                  className="mm"
                  style={{ margin: '12px 0 0', fontSize: 11.5 }}
                  data-testid={`confidence-${r.id}`}
                >
                  Flagged at extraction:{' '}
                  {CONFIDENCE_REASON[r.confidenceReason ?? 'OTHER'] ?? CONFIDENCE_REASON.OTHER}{' '}
                  You confirmed it anyway.
                </p>
              ) : (
                <p
                  style={{ margin: '12px 0 0', fontSize: 12.5, fontWeight: 600, color: 'var(--warn)' }}
                  data-testid={`confidence-${r.id}`}
                >
                  {r.confidenceBand === 'AMBIGUOUS' ? 'Ambiguous' : 'Needs review'} —{' '}
                  <span style={{ fontWeight: 400, color: 'var(--ink)' }}>
                    {CONFIDENCE_REASON[r.confidenceReason ?? 'OTHER'] ?? CONFIDENCE_REASON.OTHER}
                  </span>
                </p>
              )
            ) : null}

            {r.needsRevisionReview ? (
              <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--danger)' }} data-testid={`needs-reapproval-${r.id}`}>
                Edited after approval — re-approve before new matching counts as authoritative.
              </p>
            ) : null}

            {r.scriptReferences && r.scriptReferences.length > 0 ? (
              <p className="mm" style={{ margin: '10px 0 0', fontSize: 12 }}>
                Script references: {r.scriptReferences.map((x) => `“${x}”`).join(', ')}
              </p>
            ) : null}

            {r.creativeDescription ? (
              <p style={{ margin: '10px 0 0', fontSize: 12.5, lineHeight: 1.6 }}>
                {r.creativeDescription}
              </p>
            ) : null}

            {r.constraints.length > 0 ? (
              <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
                {(['MUST_HAVE', 'NICE_TO_HAVE', 'EXCLUDE'] as const).map((kind) => {
                  const items = r.constraints.filter((c) => c.kind === kind);
                  if (items.length === 0) return null;
                  return (
                    <p key={kind} style={{ margin: 0, fontSize: 12 }}>
                      <span className="flbl" style={{ display: 'inline', marginRight: 8 }}>
                        {CONSTRAINT_HEADING[kind]}
                      </span>
                      {items.map((c) => c.label).join(' · ')}
                    </p>
                  );
                })}
              </div>
            ) : null}

            {r.searchCountry ?? r.searchCity ? (
              <p className="mm" style={{ margin: '10px 0 0', fontSize: 11.5 }}>
                Search area: {[r.searchCity, r.searchCountry].filter(Boolean).join(' · ')}
                {r.searchRadiusKm ? ` + ${String(r.searchRadiusKm)} km` : ''}
              </p>
            ) : null}

            {/* Actions. Approval and sourcing are SEPARATE buttons because they
                are separate decisions — approving twelve requirements in a
                planning session has not authorised twelve searches. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              {r.status !== 'APPROVED' && r.status !== 'DISMISSED' ? (
                <button
                  type="button"
                  className="ctl"
                  data-testid={`approve-${r.id}`}
                  disabled={approve.isPending}
                  onClick={() => {
                    approve.mutate(r);
                  }}
                >
                  We need this location
                </button>
              ) : null}

              {r.status === 'APPROVED' ? (
                r.sourcingStatus === 'ACTIVE' ? (
                  <button
                    type="button"
                    className="ctl"
                    data-testid={`pause-${r.id}`}
                    onClick={() => {
                      sourcing.mutate({ r, to: 'PAUSED' });
                    }}
                  >
                    Pause searching
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ctl"
                    data-testid={`activate-${r.id}`}
                    onClick={() => {
                      sourcing.mutate({ r, to: 'ACTIVE' });
                    }}
                  >
                    {/* Resume vs start: the label doubles as a statement of
                        which state the requirement is in, so a producer does
                        not have to read the badge to know. */}
                    {r.sourcingStatus === 'PAUSED' ? 'Resume searching' : 'Start searching'}
                  </button>
                )
              ) : null}

              {r.status !== 'DISMISSED' ? (
                dismissing === r.id ? (
                  // One tap says why; "Dismiss without a reason" dismisses anyway, so
                  // asking never stands between a producer and the decision itself.
                  // "Keep it" backs out and sits apart from every option that dismisses.
                  <span
                    role="group"
                    aria-labelledby={`dismiss-question-${r.id}`}
                    data-testid={`dismiss-reasons-${r.id}`}
                    style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}
                  >
                    <span
                      id={`dismiss-question-${r.id}`}
                      className="mm"
                      style={{ fontSize: 12 }}
                    >
                      Why is this not needed?
                    </span>
                    {DISMISSAL_REASONS.map(([code, label], index) => (
                      <button
                        key={code}
                        ref={index === 0 ? firstReasonRef : undefined}
                        type="button"
                        className="ctl"
                        data-testid={`dismiss-reason-${r.id}-${code}`}
                        disabled={dismiss.isPending}
                        onClick={() => {
                          dismiss.mutate({ r, reasonCode: code });
                        }}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="ctl"
                      data-testid={`dismiss-reason-${r.id}-NONE`}
                      disabled={dismiss.isPending}
                      onClick={() => {
                        dismiss.mutate({ r, reasonCode: null });
                      }}
                    >
                      Dismiss without a reason
                    </button>
                    <span
                      aria-hidden="true"
                      data-testid={`dismiss-separator-${r.id}`}
                      style={{
                        alignSelf: 'stretch',
                        borderLeft: '1px solid var(--color-divider)',
                        margin: '0 4px',
                      }}
                    />
                    <button
                      type="button"
                      className="ctl"
                      data-testid={`dismiss-cancel-${r.id}`}
                      disabled={dismiss.isPending}
                      onClick={() => {
                        setDismissing(null);
                      }}
                    >
                      Keep it
                    </button>
                  </span>
                ) : (
                  <button
                    ref={(element) => {
                      if (element === null) dismissButtons.current.delete(r.id);
                      else dismissButtons.current.set(r.id, element);
                    }}
                    type="button"
                    className="ctl"
                    data-testid={`dismiss-${r.id}`}
                    disabled={dismiss.isPending}
                    onClick={() => {
                      setDismissing(r.id);
                    }}
                  >
                    We don&apos;t need this
                  </button>
                )
              ) : (
                <span className="mm" style={{ fontSize: 12, alignSelf: 'center' }}>
                  {/* Dismissal is remembered: re-extraction will not resurrect it
                      under a new name. */}
                  Dismissed — re-extraction will not propose this again.
                </span>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
